/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { EnvironmentProviders, importProvidersFrom, makeEnvironmentProviders } from '@angular/core';
import { getApp, provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { getFirestore, provideFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { getStorage, provideStorage, connectStorageEmulator } from '@angular/fire/storage';
import { getAuth, provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import {
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
  provideAppCheck
} from '@angular/fire/app-check';
// import { getAnalytics, provideAnalytics, isSupported, Analytics } from '@angular/fire/analytics';
import { CONFIG } from '@angular/fire/compat/analytics';
import { environment } from '@env/environment';

declare global {
  // eslint-disable-next-line no-var
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
}

export const OSKFirebaseProvider: EnvironmentProviders = makeEnvironmentProviders([
  importProvidersFrom(
    provideFirebaseApp(() => initializeApp({ ...environment.firebaseConfig })),
    provideAppCheck(() => {
      const app = getApp();
      app.automaticDataCollectionEnabled = false;

      self.FIREBASE_APPCHECK_DEBUG_TOKEN = environment.reCaptcha.token || false;
      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(environment.reCaptcha.siteCode),
        isTokenAutoRefreshEnabled: environment.reCaptcha.token === undefined
      });
      return appCheck;
    }),
    // provideAnalytics(() => {
    //     const analytics = getAnalytics();
    //     if (environment.useEmulator && environment.firebaseEmulatorConfig) {
    //         analytics.app.automaticDataCollectionEnabled = false;
    //     }
    //     return getAnalytics();
    // }),
    provideAuth(() => {
      const auth = getAuth();
      auth.useDeviceLanguage();
      if (environment.useEmulator && environment.firebaseEmulatorConfig) {
        connectAuthEmulator(auth, environment.firebaseEmulatorConfig.auth, {
          disableWarnings: true
        });
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulator && environment.firebaseEmulatorConfig) {
        connectFirestoreEmulator(
          firestore,
          environment.firebaseEmulatorConfig.cloudFirestore.host,
          environment.firebaseEmulatorConfig.cloudFirestore.port
        );
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions(undefined, 'europe-west1');
      if (environment.useEmulator && environment.firebaseEmulatorConfig) {
        connectFunctionsEmulator(
          functions,
          environment.firebaseEmulatorConfig.cloudFunctions.host,
          environment.firebaseEmulatorConfig.cloudFunctions.port
        );
      }
      return functions;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulator && environment.firebaseEmulatorConfig) {
        connectStorageEmulator(
          storage,
          environment.firebaseEmulatorConfig.cloudStorage.host,
          environment.firebaseEmulatorConfig.cloudStorage.port
        );
      }
      return storage;
    })
  ),
  {
    provide: CONFIG,
    useValue: {
      send_page_view: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      anonymize_ip: true
    }
  }
]);
